/**
 * name: @storyblok/vue
 * (c) 2025
 * description: SDK to integrate Storyblok into your project using Vue.
 * author: Storyblok
 */
import { defineComponent as K, ref as O, resolveDynamicComponent as M, inject as ae, createBlock as J, openBlock as Y, mergeProps as ie, h as W, createTextVNode as le, watch as ce, onMounted as he, defineAsyncComponent as ue } from "vue";
let D = !1;
const z = [], de = (r) => new Promise((e, t) => {
  if (typeof window > "u" || (window.storyblokRegisterEvent = (o) => {
    if (window.location === window.parent.location) {
      console.warn("You are not in Draft Mode or in the Visual Editor.");
      return;
    }
    D ? o() : z.push(o);
  }, document.getElementById("storyblok-javascript-bridge")))
    return;
  const s = document.createElement("script");
  s.async = !0, s.src = r, s.id = "storyblok-javascript-bridge", s.onerror = (o) => t(o), s.onload = (o) => {
    z.forEach((n) => n()), D = !0, e(o);
  }, document.getElementsByTagName("head")[0].appendChild(s);
});
var pe = Object.defineProperty, ge = (r, e, t) => e in r ? pe(r, e, { enumerable: !0, configurable: !0, writable: !0, value: t }) : r[e] = t, p = (r, e, t) => ge(r, typeof e != "symbol" ? e + "" : e, t);
class me extends Error {
  constructor(e) {
    super(e), this.name = "AbortError";
  }
}
function fe(r, e, t) {
  if (!Number.isFinite(e))
    throw new TypeError("Expected `limit` to be a finite number");
  if (!Number.isFinite(t))
    throw new TypeError("Expected `interval` to be a finite number");
  const s = [];
  let o = [], n = 0, a = !1;
  const l = async () => {
    n++;
    const h = s.shift();
    if (h)
      try {
        const d = await r(...h.args);
        h.resolve(d);
      } catch (d) {
        h.reject(d);
      }
    const u = setTimeout(() => {
      n--, s.length > 0 && l(), o = o.filter((d) => d !== u);
    }, t);
    o.includes(u) || o.push(u);
  }, i = (...h) => a ? Promise.reject(
    new Error(
      "Throttled function is already aborted and not accepting new promises"
    )
  ) : new Promise((u, d) => {
    s.push({
      resolve: u,
      reject: d,
      args: h
    }), n < e && l();
  });
  return i.abort = () => {
    a = !0, o.forEach(clearTimeout), o = [], s.forEach(
      (h) => h.reject(() => new me("Throttle function aborted"))
    ), s.length = 0;
  }, i;
}
let L = class {
  constructor() {
    p(this, "isCDNUrl", (r = "") => r.includes("/cdn/")), p(this, "getOptionsPage", (r, e = 25, t = 1) => ({
      ...r,
      per_page: e,
      page: t
    })), p(this, "delay", (r) => new Promise((e) => setTimeout(e, r))), p(this, "arrayFrom", (r = 0, e) => Array.from({ length: r }, e)), p(this, "range", (r = 0, e = r) => {
      const t = Math.abs(e - r) || 0, s = r < e ? 1 : -1;
      return this.arrayFrom(t, (o, n) => n * s + r);
    }), p(this, "asyncMap", async (r, e) => Promise.all(r.map(e))), p(this, "flatMap", (r = [], e) => r.map(e).reduce((t, s) => [...t, ...s], [])), p(this, "escapeHTML", function(r) {
      const e = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;"
      }, t = /[&<>"']/g, s = new RegExp(t.source);
      return r && s.test(r) ? r.replace(t, (o) => e[o]) : r;
    });
  }
  /**
   * @method stringify
   * @param  {object} params
   * @param  {string} prefix
   * @param  {boolean} isArray
   * @return {string} Stringified object
   */
  stringify(r, e, t) {
    const s = [];
    for (const o in r) {
      if (!Object.prototype.hasOwnProperty.call(r, o))
        continue;
      const n = r[o], a = t ? "" : encodeURIComponent(o);
      let l;
      typeof n == "object" ? l = this.stringify(
        n,
        e ? e + encodeURIComponent(`[${a}]`) : a,
        Array.isArray(n)
      ) : l = `${e ? e + encodeURIComponent(`[${a}]`) : a}=${encodeURIComponent(n)}`, s.push(l);
    }
    return s.join("&");
  }
  /**
   * @method getRegionURL
   * @param  {string} regionCode region code, could be eu, us, cn, ap or ca
   * @return {string} The base URL of the region
   */
  getRegionURL(r) {
    const e = "api.storyblok.com", t = "api-us.storyblok.com", s = "app.storyblokchina.cn", o = "api-ap.storyblok.com", n = "api-ca.storyblok.com";
    switch (r) {
      case "us":
        return t;
      case "cn":
        return s;
      case "ap":
        return o;
      case "ca":
        return n;
      default:
        return e;
    }
  }
};
const ye = function(r, e) {
  const t = {};
  for (const s in r) {
    const o = r[s];
    e.includes(s) && o !== null && (t[s] = o);
  }
  return t;
}, be = (r) => r === "email", ke = () => ({
  singleTag: "hr"
}), ve = () => ({
  tag: "blockquote"
}), $e = () => ({
  tag: "ul"
}), Te = (r) => ({
  tag: [
    "pre",
    {
      tag: "code",
      attrs: r.attrs
    }
  ]
}), we = () => ({
  singleTag: "br"
}), Re = (r) => ({
  tag: `h${r.attrs.level}`
}), _e = (r) => ({
  singleTag: [
    {
      tag: "img",
      attrs: ye(r.attrs, ["src", "alt", "title"])
    }
  ]
}), Se = () => ({
  tag: "li"
}), Ee = () => ({
  tag: "ol"
}), je = () => ({
  tag: "p"
}), Ie = (r) => ({
  tag: [
    {
      tag: "span",
      attrs: {
        "data-type": "emoji",
        "data-name": r.attrs.name,
        emoji: r.attrs.emoji
      }
    }
  ]
}), xe = () => ({
  tag: "b"
}), Ae = () => ({
  tag: "s"
}), Ce = () => ({
  tag: "u"
}), Oe = () => ({
  tag: "strong"
}), Le = () => ({
  tag: "code"
}), Pe = () => ({
  tag: "i"
}), Ne = (r) => {
  if (!r.attrs)
    return {
      tag: ""
    };
  const e = new L().escapeHTML, t = { ...r.attrs }, { linktype: s = "url" } = r.attrs;
  if (delete t.linktype, t.href && (t.href = e(r.attrs.href || "")), be(s) && (t.href = `mailto:${t.href}`), t.anchor && (t.href = `${t.href}#${t.anchor}`, delete t.anchor), t.custom) {
    for (const o in t.custom)
      t[o] = t.custom[o];
    delete t.custom;
  }
  return {
    tag: [
      {
        tag: "a",
        attrs: t
      }
    ]
  };
}, Me = (r) => ({
  tag: [
    {
      tag: "span",
      attrs: r.attrs
    }
  ]
}), Ue = () => ({
  tag: "sub"
}), He = () => ({
  tag: "sup"
}), De = (r) => ({
  tag: [
    {
      tag: "span",
      attrs: r.attrs
    }
  ]
}), ze = (r) => {
  var e;
  return (e = r.attrs) != null && e.color ? {
    tag: [
      {
        tag: "span",
        attrs: {
          style: `background-color:${r.attrs.color};`
        }
      }
    ]
  } : {
    tag: ""
  };
}, Fe = (r) => {
  var e;
  return (e = r.attrs) != null && e.color ? {
    tag: [
      {
        tag: "span",
        attrs: {
          style: `color:${r.attrs.color}`
        }
      }
    ]
  } : {
    tag: ""
  };
}, Be = {
  nodes: {
    horizontal_rule: ke,
    blockquote: ve,
    bullet_list: $e,
    code_block: Te,
    hard_break: we,
    heading: Re,
    image: _e,
    list_item: Se,
    ordered_list: Ee,
    paragraph: je,
    emoji: Ie
  },
  marks: {
    bold: xe,
    strike: Ae,
    underline: Ce,
    strong: Oe,
    code: Le,
    italic: Pe,
    link: Ne,
    styled: Me,
    subscript: Ue,
    superscript: He,
    anchor: De,
    highlight: ze,
    textStyle: Fe
  }
}, qe = function(r) {
  const e = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  }, t = /[&<>"']/g, s = new RegExp(t.source);
  return r && s.test(r) ? r.replace(t, (o) => e[o]) : r;
};
let F = !1;
class P {
  constructor(e) {
    p(this, "marks"), p(this, "nodes"), e || (e = Be), this.marks = e.marks || [], this.nodes = e.nodes || [];
  }
  addNode(e, t) {
    this.nodes[e] = t;
  }
  addMark(e, t) {
    this.marks[e] = t;
  }
  render(e, t = { optimizeImages: !1 }, s = !0) {
    if (!F && s && (console.warn(
      "Warning ⚠️: The RichTextResolver class is deprecated and will be removed in the next major release. Please use the `@storyblok/richtext` package instead. https://github.com/storyblok/richtext/"
    ), F = !0), e && e.content && Array.isArray(e.content)) {
      let o = "";
      return e.content.forEach((n) => {
        o += this.renderNode(n);
      }), t.optimizeImages ? this.optimizeImages(o, t.optimizeImages) : o;
    }
    return console.warn(
      `The render method must receive an Object with a "content" field.
      The "content" field must be an array of nodes as the type ISbRichtext.
      ISbRichtext:
        content?: ISbRichtext[]
        marks?: ISbRichtext[]
        attrs?: any
        text?: string
        type: string
        
        Example:
        {
          content: [
            {
              content: [
                {
                  text: 'Hello World',
                  type: 'text'
                }
              ],
              type: 'paragraph'
            }
          ],
          type: 'doc'
        }`
    ), "";
  }
  optimizeImages(e, t) {
    let s = 0, o = 0, n = "", a = "";
    typeof t != "boolean" && (typeof t.width == "number" && t.width > 0 && (n += `width="${t.width}" `, s = t.width), typeof t.height == "number" && t.height > 0 && (n += `height="${t.height}" `, o = t.height), (t.loading === "lazy" || t.loading === "eager") && (n += `loading="${t.loading}" `), typeof t.class == "string" && t.class.length > 0 && (n += `class="${t.class}" `), t.filters && (typeof t.filters.blur == "number" && t.filters.blur >= 0 && t.filters.blur <= 100 && (a += `:blur(${t.filters.blur})`), typeof t.filters.brightness == "number" && t.filters.brightness >= -100 && t.filters.brightness <= 100 && (a += `:brightness(${t.filters.brightness})`), t.filters.fill && (t.filters.fill.match(/[0-9A-F]{6}/gi) || t.filters.fill === "transparent") && (a += `:fill(${t.filters.fill})`), t.filters.format && ["webp", "png", "jpeg"].includes(t.filters.format) && (a += `:format(${t.filters.format})`), typeof t.filters.grayscale == "boolean" && t.filters.grayscale && (a += ":grayscale()"), typeof t.filters.quality == "number" && t.filters.quality >= 0 && t.filters.quality <= 100 && (a += `:quality(${t.filters.quality})`), t.filters.rotate && [90, 180, 270].includes(t.filters.rotate) && (a += `:rotate(${t.filters.rotate})`), a.length > 0 && (a = `/filters${a}`))), n.length > 0 && (e = e.replace(/<img/g, `<img ${n.trim()}`));
    const l = s > 0 || o > 0 || a.length > 0 ? `${s}x${o}${a}` : "";
    return e = e.replace(
      /a.storyblok.com\/f\/(\d+)\/([^.]+)\.(gif|jpg|jpeg|png|tif|bmp)/g,
      `a.storyblok.com/f/$1/$2.$3/m/${l}`
    ), typeof t != "boolean" && (t.sizes || t.srcset) && (e = e.replace(/<img.*?src=["|'](.*?)["|']/g, (i) => {
      var h, u;
      const d = i.match(
        /a.storyblok.com\/f\/(\d+)\/([^.]+)\.(gif|jpg|jpeg|png|tif|bmp)/g
      );
      if (d && d.length > 0) {
        const f = {
          srcset: (h = t.srcset) == null ? void 0 : h.map((b) => {
            if (typeof b == "number")
              return `//${d}/m/${b}x0${a} ${b}w`;
            if (typeof b == "object" && b.length === 2) {
              let _ = 0, j = 0;
              return typeof b[0] == "number" && (_ = b[0]), typeof b[1] == "number" && (j = b[1]), `//${d}/m/${_}x${j}${a} ${_}w`;
            }
            return "";
          }).join(", "),
          sizes: (u = t.sizes) == null ? void 0 : u.map((b) => b).join(", ")
        };
        let y = "";
        return f.srcset && (y += `srcset="${f.srcset}" `), f.sizes && (y += `sizes="${f.sizes}" `), i.replace(/<img/g, `<img ${y.trim()}`);
      }
      return i;
    })), e;
  }
  renderNode(e) {
    const t = [];
    e.marks && e.marks.forEach((o) => {
      const n = this.getMatchingMark(o);
      n && n.tag !== "" && t.push(this.renderOpeningTag(n.tag));
    });
    const s = this.getMatchingNode(e);
    return s && s.tag && t.push(this.renderOpeningTag(s.tag)), e.content ? e.content.forEach((o) => {
      t.push(this.renderNode(o));
    }) : e.text ? t.push(qe(e.text)) : s && s.singleTag ? t.push(this.renderTag(s.singleTag, " /")) : s && s.html ? t.push(s.html) : e.type === "emoji" && t.push(this.renderEmoji(e)), s && s.tag && t.push(this.renderClosingTag(s.tag)), e.marks && e.marks.slice(0).reverse().forEach((o) => {
      const n = this.getMatchingMark(o);
      n && n.tag !== "" && t.push(this.renderClosingTag(n.tag));
    }), t.join("");
  }
  renderTag(e, t) {
    return e.constructor === String ? `<${e}${t}>` : e.map((s) => {
      if (s.constructor === String)
        return `<${s}${t}>`;
      {
        let o = `<${s.tag}`;
        if (s.attrs) {
          for (const n in s.attrs)
            if (Object.prototype.hasOwnProperty.call(s.attrs, n)) {
              const a = s.attrs[n];
              a !== null && (o += ` ${n}="${a}"`);
            }
        }
        return `${o}${t}>`;
      }
    }).join("");
  }
  renderOpeningTag(e) {
    return this.renderTag(e, "");
  }
  renderClosingTag(e) {
    return e.constructor === String ? `</${e}>` : e.slice(0).reverse().map((t) => t.constructor === String ? `</${t}>` : `</${t.tag}>`).join("");
  }
  getMatchingNode(e) {
    const t = this.nodes[e.type];
    if (typeof t == "function")
      return t(e);
  }
  getMatchingMark(e) {
    const t = this.marks[e.type];
    if (typeof t == "function")
      return t(e);
  }
  renderEmoji(e) {
    if (e.attrs.emoji)
      return e.attrs.emoji;
    const t = [
      {
        tag: "img",
        attrs: {
          src: e.attrs.fallbackImage,
          draggable: "false",
          loading: "lazy",
          align: "absmiddle"
        }
      }
    ];
    return this.renderTag(t, " /");
  }
}
class Ve {
  constructor(e) {
    p(this, "baseURL"), p(this, "timeout"), p(this, "headers"), p(this, "responseInterceptor"), p(this, "fetch"), p(this, "ejectInterceptor"), p(this, "url"), p(this, "parameters"), p(this, "fetchOptions"), this.baseURL = e.baseURL, this.headers = e.headers || new Headers(), this.timeout = e != null && e.timeout ? e.timeout * 1e3 : 0, this.responseInterceptor = e.responseInterceptor, this.fetch = (...t) => e.fetch ? e.fetch(...t) : fetch(...t), this.ejectInterceptor = !1, this.url = "", this.parameters = {}, this.fetchOptions = {};
  }
  /**
   *
   * @param url string
   * @param params ISbStoriesParams
   * @returns Promise<ISbResponse | Error>
   */
  get(e, t) {
    return this.url = e, this.parameters = t, this._methodHandler("get");
  }
  post(e, t) {
    return this.url = e, this.parameters = t, this._methodHandler("post");
  }
  put(e, t) {
    return this.url = e, this.parameters = t, this._methodHandler("put");
  }
  delete(e, t) {
    return this.url = e, this.parameters = t ?? {}, this._methodHandler("delete");
  }
  async _responseHandler(e) {
    const t = [], s = {
      data: {},
      headers: {},
      status: 0,
      statusText: ""
    };
    e.status !== 204 && await e.json().then((o) => {
      s.data = o;
    });
    for (const o of e.headers.entries())
      t[o[0]] = o[1];
    return s.headers = { ...t }, s.status = e.status, s.statusText = e.statusText, s;
  }
  async _methodHandler(e) {
    let t = `${this.baseURL}${this.url}`, s = null;
    if (e === "get") {
      const i = new L();
      t = `${this.baseURL}${this.url}?${i.stringify(
        this.parameters
      )}`;
    } else
      s = JSON.stringify(this.parameters);
    const o = new URL(t), n = new AbortController(), { signal: a } = n;
    let l;
    this.timeout && (l = setTimeout(() => n.abort(), this.timeout));
    try {
      const i = await this.fetch(`${o}`, {
        method: e,
        headers: this.headers,
        body: s,
        signal: a,
        ...this.fetchOptions
      });
      this.timeout && clearTimeout(l);
      const h = await this._responseHandler(
        i
      );
      return this.responseInterceptor && !this.ejectInterceptor ? this._statusHandler(this.responseInterceptor(h)) : this._statusHandler(h);
    } catch (i) {
      return {
        message: i
      };
    }
  }
  setFetchOptions(e = {}) {
    Object.keys(e).length > 0 && "method" in e && delete e.method, this.fetchOptions = { ...e };
  }
  eject() {
    this.ejectInterceptor = !0;
  }
  _statusHandler(e) {
    const t = /20[0-6]/g;
    return new Promise((s, o) => {
      if (t.test(`${e.status}`))
        return s(e);
      const n = {
        message: e.statusText,
        status: e.status,
        response: Array.isArray(e.data) ? e.data[0] : e.data.error || e.data.slug
      };
      o(n);
    });
  }
}
const B = "SB-Agent", U = {
  defaultAgentName: "SB-JS-CLIENT",
  defaultAgentVersion: "SB-Agent-Version",
  packageVersion: "6.0.0"
};
let C = {};
const S = {};
class Ge {
  /**
   *
   * @param config ISbConfig interface
   * @param pEndpoint string, optional
   */
  constructor(e, t) {
    p(this, "client"), p(this, "maxRetries"), p(this, "retriesDelay"), p(this, "throttle"), p(this, "accessToken"), p(this, "cache"), p(this, "helpers"), p(this, "resolveCounter"), p(this, "relations"), p(this, "links"), p(this, "richTextResolver"), p(this, "resolveNestedRelations"), p(this, "stringifiedStoriesCache");
    let s = e.endpoint || t;
    if (!s) {
      const a = new L().getRegionURL, l = e.https === !1 ? "http" : "https";
      e.oauthToken ? s = `${l}://${a(e.region)}/v1` : s = `${l}://${a(e.region)}/v2`;
    }
    const o = new Headers();
    o.set("Content-Type", "application/json"), o.set("Accept", "application/json"), e.headers && (e.headers.constructor.name === "Headers" ? e.headers.entries().toArray() : Object.entries(e.headers)).forEach(([a, l]) => {
      o.set(a, l);
    }), o.has(B) || (o.set(B, U.defaultAgentName), o.set(
      U.defaultAgentVersion,
      U.packageVersion
    ));
    let n = 5;
    e.oauthToken && (o.set("Authorization", e.oauthToken), n = 3), e.rateLimit && (n = e.rateLimit), e.richTextSchema ? this.richTextResolver = new P(e.richTextSchema) : this.richTextResolver = new P(), e.componentResolver && this.setComponentResolver(e.componentResolver), this.maxRetries = e.maxRetries || 10, this.retriesDelay = 300, this.throttle = fe(
      this.throttledRequest.bind(this),
      n,
      1e3
    ), this.accessToken = e.accessToken || "", this.relations = {}, this.links = {}, this.cache = e.cache || { clear: "manual" }, this.helpers = new L(), this.resolveCounter = 0, this.resolveNestedRelations = e.resolveNestedRelations || !0, this.stringifiedStoriesCache = {}, this.client = new Ve({
      baseURL: s,
      timeout: e.timeout || 0,
      headers: o,
      responseInterceptor: e.responseInterceptor,
      fetch: e.fetch
    });
  }
  setComponentResolver(e) {
    this.richTextResolver.addNode("blok", (t) => {
      let s = "";
      return t.attrs.body && t.attrs.body.forEach((o) => {
        s += e(o.component, o);
      }), {
        html: s
      };
    });
  }
  parseParams(e) {
    return e.token || (e.token = this.getToken()), e.cv || (e.cv = S[e.token]), Array.isArray(e.resolve_relations) && (e.resolve_relations = e.resolve_relations.join(",")), typeof e.resolve_relations < "u" && (e.resolve_level = 2), e;
  }
  factoryParamOptions(e, t) {
    return this.helpers.isCDNUrl(e) ? this.parseParams(t) : t;
  }
  makeRequest(e, t, s, o, n) {
    const a = this.factoryParamOptions(
      e,
      this.helpers.getOptionsPage(t, s, o)
    );
    return this.cacheResponse(e, a, void 0, n);
  }
  get(e, t, s) {
    t || (t = {});
    const o = `/${e}`, n = this.factoryParamOptions(o, t);
    return this.cacheResponse(o, n, void 0, s);
  }
  async getAll(e, t, s, o) {
    const n = (t == null ? void 0 : t.per_page) || 25, a = `/${e}`.replace(/\/$/, ""), l = s ?? a.substring(a.lastIndexOf("/") + 1), i = 1, h = await this.makeRequest(
      a,
      t,
      n,
      i,
      o
    ), u = h.total ? Math.ceil(h.total / n) : 1, d = await this.helpers.asyncMap(
      this.helpers.range(i, u),
      (f) => this.makeRequest(a, t, n, f + 1, o)
    );
    return this.helpers.flatMap([h, ...d], (f) => Object.values(f.data[l]));
  }
  post(e, t, s) {
    const o = `/${e}`;
    return Promise.resolve(
      this.throttle("post", o, t, s)
    );
  }
  put(e, t, s) {
    const o = `/${e}`;
    return Promise.resolve(
      this.throttle("put", o, t, s)
    );
  }
  delete(e, t, s) {
    t || (t = {});
    const o = `/${e}`;
    return Promise.resolve(
      this.throttle("delete", o, t, s)
    );
  }
  getStories(e, t) {
    return this._addResolveLevel(e), this.get("cdn/stories", e, t);
  }
  getStory(e, t, s) {
    return this._addResolveLevel(t), this.get(`cdn/stories/${e}`, t, s);
  }
  getToken() {
    return this.accessToken;
  }
  ejectInterceptor() {
    this.client.eject();
  }
  _addResolveLevel(e) {
    typeof e.resolve_relations < "u" && (e.resolve_level = 2);
  }
  _cleanCopy(e) {
    return JSON.parse(JSON.stringify(e));
  }
  _insertLinks(e, t, s) {
    const o = e[t];
    o && o.fieldtype === "multilink" && o.linktype === "story" && typeof o.id == "string" && this.links[s][o.id] ? o.story = this._cleanCopy(this.links[s][o.id]) : o && o.linktype === "story" && typeof o.uuid == "string" && this.links[s][o.uuid] && (o.story = this._cleanCopy(this.links[s][o.uuid]));
  }
  /**
   *
   * @param resolveId A counter number as a string
   * @param uuid The uuid of the story
   * @returns string | object
   */
  getStoryReference(e, t) {
    return this.relations[e][t] ? JSON.parse(this.stringifiedStoriesCache[t] || JSON.stringify(this.relations[e][t])) : t;
  }
  /**
   * Resolves a field's value by replacing UUIDs with their corresponding story references
   * @param jtree - The JSON tree object containing the field to resolve
   * @param treeItem - The key of the field to resolve
   * @param resolveId - The unique identifier for the current resolution context
   *
   * This method handles both single string UUIDs and arrays of UUIDs:
   * - For single strings: directly replaces the UUID with the story reference
   * - For arrays: maps through each UUID and replaces with corresponding story references
   */
  _resolveField(e, t, s) {
    const o = e[t];
    typeof o == "string" ? e[t] = this.getStoryReference(s, o) : Array.isArray(o) && (e[t] = o.map(
      (n) => this.getStoryReference(s, n)
    ).filter(Boolean));
  }
  /**
   * Inserts relations into the JSON tree by resolving references
   * @param jtree - The JSON tree object to process
   * @param treeItem - The current field being processed
   * @param fields - The relation patterns to resolve (string or array of strings)
   * @param resolveId - The unique identifier for the current resolution context
   *
   * This method handles two types of relation patterns:
   * 1. Nested relations: matches fields that end with the current field name
   *    Example: If treeItem is "event_type", it matches patterns like "*.event_type"
   *
   * 2. Direct component relations: matches exact component.field patterns
   *    Example: "event.event_type" for component "event" and field "event_type"
   *
   * The method supports both string and array formats for the fields parameter,
   * allowing flexible specification of relation patterns.
   */
  _insertRelations(e, t, s, o) {
    if (Array.isArray(s) ? s.find((a) => a.endsWith(`.${t}`)) : s.endsWith(`.${t}`)) {
      this._resolveField(e, t, o);
      return;
    }
    const n = e.component ? `${e.component}.${t}` : t;
    (Array.isArray(s) ? s.includes(n) : s === n) && this._resolveField(e, t, o);
  }
  /**
   * Recursively traverses and resolves relations in the story content tree
   * @param story - The story object containing the content to process
   * @param fields - The relation patterns to resolve
   * @param resolveId - The unique identifier for the current resolution context
   */
  iterateTree(e, t, s) {
    const o = (n, a = "") => {
      if (!(!n || n._stopResolving)) {
        if (Array.isArray(n))
          n.forEach((l, i) => o(l, `${a}[${i}]`));
        else if (typeof n == "object")
          for (const l in n) {
            const i = a ? `${a}.${l}` : l;
            (n.component && n._uid || n.type === "link") && (this._insertRelations(n, l, t, s), this._insertLinks(n, l, s)), o(n[l], i);
          }
      }
    };
    o(e.content);
  }
  async resolveLinks(e, t, s) {
    let o = [];
    if (e.link_uuids) {
      const n = e.link_uuids.length, a = [], l = 50;
      for (let i = 0; i < n; i += l) {
        const h = Math.min(n, i + l);
        a.push(e.link_uuids.slice(i, h));
      }
      for (let i = 0; i < a.length; i++)
        (await this.getStories({
          per_page: l,
          language: t.language,
          version: t.version,
          by_uuids: a[i].join(",")
        })).data.stories.forEach(
          (h) => {
            o.push(h);
          }
        );
    } else
      o = e.links;
    o.forEach((n) => {
      this.links[s][n.uuid] = {
        ...n,
        _stopResolving: !0
      };
    });
  }
  async resolveRelations(e, t, s) {
    let o = [];
    if (e.rel_uuids) {
      const n = e.rel_uuids.length, a = [], l = 50;
      for (let i = 0; i < n; i += l) {
        const h = Math.min(n, i + l);
        a.push(e.rel_uuids.slice(i, h));
      }
      for (let i = 0; i < a.length; i++)
        (await this.getStories({
          per_page: l,
          language: t.language,
          version: t.version,
          by_uuids: a[i].join(","),
          excluding_fields: t.excluding_fields
        })).data.stories.forEach((h) => {
          o.push(h);
        });
    } else
      o = e.rels;
    o && o.length > 0 && o.forEach((n) => {
      this.relations[s][n.uuid] = {
        ...n,
        _stopResolving: !0
      };
    });
  }
  /**
   *
   * @param responseData
   * @param params
   * @param resolveId
   * @description Resolves the relations and links of the stories
   * @returns Promise<void>
   *
   */
  async resolveStories(e, t, s) {
    var o, n;
    let a = [];
    if (this.links[s] = {}, this.relations[s] = {}, typeof t.resolve_relations < "u" && t.resolve_relations.length > 0 && (typeof t.resolve_relations == "string" && (a = t.resolve_relations.split(",")), await this.resolveRelations(e, t, s)), t.resolve_links && ["1", "story", "url", "link"].includes(t.resolve_links) && ((o = e.links) != null && o.length || (n = e.link_uuids) != null && n.length) && await this.resolveLinks(e, t, s), this.resolveNestedRelations)
      for (const l in this.relations[s])
        this.iterateTree(
          this.relations[s][l],
          a,
          s
        );
    e.story ? this.iterateTree(e.story, a, s) : e.stories.forEach((l) => {
      this.iterateTree(l, a, s);
    }), this.stringifiedStoriesCache = {}, delete this.links[s], delete this.relations[s];
  }
  async cacheResponse(e, t, s, o) {
    const n = this.helpers.stringify({ url: e, params: t }), a = this.cacheProvider();
    if (t.version === "published" && e !== "/cdn/spaces/me") {
      const l = await a.get(n);
      if (l)
        return Promise.resolve(l);
    }
    return new Promise(async (l, i) => {
      var h;
      try {
        const u = await this.throttle(
          "get",
          e,
          t,
          o
        );
        if (u.status !== 200)
          return i(u);
        let d = { data: u.data, headers: u.headers };
        if ((h = u.headers) != null && h["per-page"] && (d = Object.assign({}, d, {
          perPage: u.headers["per-page"] ? Number.parseInt(u.headers["per-page"]) : 0,
          total: u.headers["per-page"] ? Number.parseInt(u.headers.total) : 0
        })), d.data.story || d.data.stories) {
          const y = this.resolveCounter = ++this.resolveCounter % 1e3;
          await this.resolveStories(d.data, t, `${y}`);
        }
        t.version === "published" && e !== "/cdn/spaces/me" && await a.set(n, d);
        const f = this.cache.clear === "onpreview" && t.version === "draft" || this.cache.clear === "auto";
        return t.token && d.data.cv && (f && S[t.token] && S[t.token] !== d.data.cv && await this.flushCache(), S[t.token] = d.data.cv), l(d);
      } catch (u) {
        if (u.response && u.status === 429 && (s = typeof s > "u" ? 0 : s + 1, s < this.maxRetries))
          return console.log(
            `Hit rate limit. Retrying in ${this.retriesDelay / 1e3} seconds.`
          ), await this.helpers.delay(this.retriesDelay), this.cacheResponse(e, t, s).then(l).catch(i);
        i(u);
      }
    });
  }
  throttledRequest(e, t, s, o) {
    return this.client.setFetchOptions(o), this.client[e](t, s);
  }
  cacheVersions() {
    return S;
  }
  cacheVersion() {
    return S[this.accessToken];
  }
  setCacheVersion(e) {
    this.accessToken && (S[this.accessToken] = e);
  }
  clearCacheVersion() {
    this.accessToken && (S[this.accessToken] = 0);
  }
  cacheProvider() {
    switch (this.cache.type) {
      case "memory":
        return {
          get(e) {
            return Promise.resolve(C[e]);
          },
          getAll() {
            return Promise.resolve(C);
          },
          set(e, t) {
            return C[e] = t, Promise.resolve(void 0);
          },
          flush() {
            return C = {}, Promise.resolve(void 0);
          }
        };
      case "custom":
        if (this.cache.custom)
          return this.cache.custom;
      // eslint-disable-next-line no-fallthrough
      default:
        return {
          get() {
            return Promise.resolve();
          },
          getAll() {
            return Promise.resolve(void 0);
          },
          set() {
            return Promise.resolve(void 0);
          },
          flush() {
            return Promise.resolve(void 0);
          }
        };
    }
  }
  async flushCache() {
    return await this.cacheProvider().flush(), this.clearCacheVersion(), this;
  }
}
const lt = (r = {}) => {
  const { apiOptions: e } = r;
  if (!e || !e.accessToken) {
    console.error(
      "You need to provide an access token to interact with Storyblok API. Read https://www.storyblok.com/docs/api/content-delivery#topics/authentication"
    );
    return;
  }
  return { storyblokApi: new Ge(e) };
}, Ke = (r) => {
  if (typeof r != "object" || typeof r._editable > "u")
    return {};
  try {
    const e = JSON.parse(
      r._editable.replace(/^<!--#storyblok#/, "").replace(/-->$/, "")
    );
    return e ? {
      "data-blok-c": JSON.stringify(e),
      "data-blok-uid": `${e.id}-${e.uid}`
    } : {};
  } catch {
    return {};
  }
};
function Je(r, e) {
  if (!e)
    return { src: r, attrs: {} };
  let t = 0, s = 0;
  const o = {}, n = [];
  function a(i, h, u, d, f) {
    typeof i != "number" || i <= h || i >= u ? console.warn(`[StoryblokRichText] - ${d.charAt(0).toUpperCase() + d.slice(1)} value must be a number between ${h} and ${u} (inclusive)`) : f.push(`${d}(${i})`);
  }
  if (typeof e == "object") {
    if (typeof e.width == "number" && e.width > 0 ? (o.width = e.width, t = e.width) : console.warn("[StoryblokRichText] - Width value must be a number greater than 0"), e.height && typeof e.height == "number" && e.height > 0 ? (o.height = e.height, s = e.height) : console.warn("[StoryblokRichText] - Height value must be a number greater than 0"), e.loading && ["lazy", "eager"].includes(e.loading) && (o.loading = e.loading), e.class && (o.class = e.class), e.filters) {
      const { filters: i } = e || {}, { blur: h, brightness: u, fill: d, format: f, grayscale: y, quality: b, rotate: _ } = i || {};
      h && a(h, 0, 100, "blur", n), b && a(b, 0, 100, "quality", n), u && a(u, 0, 100, "brightness", n), d && n.push(`fill(${d})`), y && n.push("grayscale()"), _ && [0, 90, 180, 270].includes(e.filters.rotate || 0) && n.push(`rotate(${_})`), f && ["webp", "png", "jpeg"].includes(f) && n.push(`format(${f})`);
    }
    e.srcset && (o.srcset = e.srcset.map((i) => {
      if (typeof i == "number")
        return `${r}/m/${i}x0/${n.length > 0 ? `filters:${n.join(":")}` : ""} ${i}w`;
      if (Array.isArray(i) && i.length === 2) {
        const [h, u] = i;
        return `${r}/m/${h}x${u}/${n.length > 0 ? `filters:${n.join(":")}` : ""} ${h}w`;
      } else {
        console.warn("[StoryblokRichText] - srcset entry must be a number or a tuple of two numbers");
        return;
      }
    }).join(", ")), e.sizes && (o.sizes = e.sizes.join(", "));
  }
  let l = `${r}/m/`;
  return t > 0 && s > 0 && (l = `${l}${t}x${s}/`), n.length > 0 && (l = `${l}filters:${n.join(":")}`), {
    src: l,
    attrs: o
  };
}
var v = /* @__PURE__ */ ((r) => (r.DOCUMENT = "doc", r.HEADING = "heading", r.PARAGRAPH = "paragraph", r.QUOTE = "blockquote", r.OL_LIST = "ordered_list", r.UL_LIST = "bullet_list", r.LIST_ITEM = "list_item", r.CODE_BLOCK = "code_block", r.HR = "horizontal_rule", r.BR = "hard_break", r.IMAGE = "image", r.EMOJI = "emoji", r.COMPONENT = "blok", r))(v || {}), T = /* @__PURE__ */ ((r) => (r.BOLD = "bold", r.STRONG = "strong", r.STRIKE = "strike", r.UNDERLINE = "underline", r.ITALIC = "italic", r.CODE = "code", r.LINK = "link", r.ANCHOR = "anchor", r.STYLED = "styled", r.SUPERSCRIPT = "superscript", r.SUBSCRIPT = "subscript", r.TEXT_STYLE = "textStyle", r.HIGHLIGHT = "highlight", r))(T || {}), X = /* @__PURE__ */ ((r) => (r.TEXT = "text", r))(X || {}), I = /* @__PURE__ */ ((r) => (r.URL = "url", r.STORY = "story", r.ASSET = "asset", r.EMAIL = "email", r))(I || {});
const Ye = [
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr"
], We = (r = {}) => Object.keys(r).map((e) => `${e}="${r[e]}"`).join(" "), Xe = (r = {}) => Object.keys(r).map((e) => `${e}: ${r[e]}`).join("; ");
function Qe(r) {
  return r.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
const q = (r) => Object.fromEntries(Object.entries(r).filter(([e, t]) => t !== void 0));
function V(r, e = {}, t) {
  const s = We(e), o = s ? `${r} ${s}` : r, n = Array.isArray(t) ? t.join("") : t || "";
  if (r) {
    if (Ye.includes(r))
      return `<${o}>`;
  } else return n;
  return `<${o}>${n}</${r}>`;
}
function Ze(r = {}) {
  let e = 0;
  const {
    renderFn: t = V,
    textFn: s = Qe,
    resolvers: o = {},
    optimizeImages: n = !1,
    keyedResolvers: a = !1
  } = r, l = t !== V, i = (c) => (g) => {
    const m = g.attrs || {};
    return a && (m.key = `${c}-${e}`), t(c, m, g.children || null);
  }, h = (c) => {
    const { src: g, alt: m, title: k, srcset: R, sizes: $ } = c.attrs || {};
    let w = g, E = {};
    if (n) {
      const { src: oe, attrs: ne } = Je(g, n);
      w = oe, E = ne;
    }
    a && (E = {
      ...E,
      key: `img-${e}`
    });
    const se = {
      src: w,
      alt: m,
      title: k,
      srcset: R,
      sizes: $,
      ...E
    };
    return t("img", q(se));
  }, u = (c) => {
    const { level: g, ...m } = c.attrs || {}, k = {
      ...m
    };
    return a && (k.key = `h${g}-${e}`), t(`h${g}`, k, c.children);
  }, d = (c) => {
    var g, m, k, R;
    const $ = t("img", {
      src: (g = c.attrs) == null ? void 0 : g.fallbackImage,
      alt: (m = c.attrs) == null ? void 0 : m.alt,
      style: "width: 1.25em; height: 1.25em; vertical-align: text-top",
      draggable: "false",
      loading: "lazy"
    }), w = {
      "data-type": "emoji",
      "data-name": (k = c.attrs) == null ? void 0 : k.name,
      "data-emoji": (R = c.attrs) == null ? void 0 : R.emoji
    };
    return a && (w.key = `emoji-${e}`), t("span", w, $);
  }, f = (c) => t("pre", {
    ...c.attrs,
    key: `code-${e}`
  }, t("code", { key: `code-${e}` }, c.children || "")), y = (c, g = !1) => ({ text: m, attrs: k }) => {
    const { class: R, id: $, ...w } = k || {}, E = g ? {
      class: R,
      id: $,
      style: Xe(w) || void 0
    } : k || {};
    return a && (E.key = `${c}-${e}`), t(c, q(E), m);
  }, b = (c) => N(c), _ = (c) => {
    const { marks: g, ...m } = c;
    return "text" in c ? g ? g.reduce(
      (k, R) => b({ ...R, text: k }),
      // Fix: Ensure render function returns a string
      b({ ...m, children: m.children })
      // Fix: Cast children to string
    ) : s(m.text) : "";
  }, j = (c) => {
    const { linktype: g, href: m, anchor: k, ...R } = c.attrs || {};
    let $ = "";
    switch (g) {
      case I.ASSET:
      case I.URL:
        $ = m;
        break;
      case I.EMAIL:
        $ = `mailto:${m}`;
        break;
      case I.STORY:
        $ = m, k && ($ = `${$}#${k}`);
        break;
      default:
        $ = m;
        break;
    }
    const w = { ...R };
    return $ && (w.href = $), a && (w.key = `a-${e}`), t("a", w, c.text);
  }, te = (c) => {
    var g, m;
    return console.warn("[StoryblokRichtText] - BLOK resolver is not available for vanilla usage"), t("span", {
      blok: (g = c == null ? void 0 : c.attrs) == null ? void 0 : g.body[0],
      id: (m = c.attrs) == null ? void 0 : m.id,
      key: `component-${e}`,
      style: "display: none"
    });
  }, re = new Map([
    [v.DOCUMENT, i("")],
    [v.HEADING, u],
    [v.PARAGRAPH, i("p")],
    [v.UL_LIST, i("ul")],
    [v.OL_LIST, i("ol")],
    [v.LIST_ITEM, i("li")],
    [v.IMAGE, h],
    [v.EMOJI, d],
    [v.CODE_BLOCK, f],
    [v.HR, i("hr")],
    [v.BR, i("br")],
    [v.QUOTE, i("blockquote")],
    [v.COMPONENT, te],
    [X.TEXT, _],
    [T.LINK, j],
    [T.ANCHOR, j],
    [T.STYLED, y("span", !0)],
    [T.BOLD, y("strong")],
    [T.TEXT_STYLE, y("span", !0)],
    [T.ITALIC, y("em")],
    [T.UNDERLINE, y("u")],
    [T.STRIKE, y("s")],
    [T.CODE, y("code")],
    [T.SUPERSCRIPT, y("sup")],
    [T.SUBSCRIPT, y("sub")],
    [T.HIGHLIGHT, y("mark")],
    ...Object.entries(o).map(([c, g]) => [c, g])
  ]);
  function A(c) {
    e += 1;
    const g = re.get(c.type);
    if (!g)
      return console.error("<Storyblok>", `No resolver found for node type ${c.type}`), "";
    if (c.type === "text")
      return g(c);
    const m = c.content ? c.content.map(N) : void 0;
    return g({
      ...c,
      children: m
      // Fix: Update the type of 'children' to Node[]
    });
  }
  function N(c) {
    return c.type === "doc" ? l ? c.content.map(A) : c.content.map(A).join("") : Array.isArray(c) ? c.map(A) : A(c);
  }
  return {
    render: N
  };
}
let H, G = "https://app.storyblok.com/f/storyblok-v2-latest.js";
const et = (r, e, t = {}) => {
  var s;
  const o = !(typeof window > "u") && typeof window.storyblokRegisterEvent < "u", n = new URL((s = window.location) == null ? void 0 : s.href).searchParams.get(
    "_storyblok"
  ), a = n !== null && +n === r;
  if (!(!o || !a)) {
    if (!r) {
      console.warn("Story ID is not defined. Please provide a valid ID.");
      return;
    }
    window.storyblokRegisterEvent(() => {
      new window.StoryblokBridge(t).on(["input", "published", "change"], (l) => {
        var i;
        l && (l.action === "input" && ((i = l.story) == null ? void 0 : i.id) === r ? e(l.story) : (l.action === "change" || l.action === "published") && l.storyId === r && window.location.reload());
      });
    });
  }
}, Q = (r, e) => {
  r.addNode("blok", (t) => {
    let s = "";
    return t.attrs.body.forEach((o) => {
      s += e(o.component, o);
    }), {
      html: s
    };
  });
}, tt = (r = {}) => {
  var e, t;
  const {
    bridge: s,
    accessToken: o,
    use: n = [],
    apiOptions: a = {},
    richText: l = {},
    bridgeUrl: i
  } = r;
  a.accessToken = a.accessToken || o;
  const h = { bridge: s, apiOptions: a };
  let u = {};
  n.forEach((f) => {
    u = { ...u, ...f(h) };
  }), i && (G = i);
  const d = !(typeof window > "u") && ((t = (e = window.location) == null ? void 0 : e.search) == null ? void 0 : t.includes("_storyblok_tk"));
  return s !== !1 && d && de(G), H = new P(l.schema), l.resolver && Q(H, l.resolver), u;
}, rt = (r) => {
  var e;
  return !r || !((e = r == null ? void 0 : r.content) != null && e.some((t) => t.content || t.type === "blok" || t.type === "horizontal_rule"));
}, ct = (r, e, t) => {
  let s = t || H;
  if (!s) {
    console.error(
      "Please initialize the Storyblok SDK before calling the renderRichText function"
    );
    return;
  }
  return rt(r) ? "" : (e && (s = new P(e.schema), e.resolver && Q(s, e.resolver)), s.render(r, {}, !1));
}, Z = /* @__PURE__ */ K({
  __name: "StoryblokComponent",
  props: {
    blok: {}
  },
  setup(r, { expose: e }) {
    const t = r, s = O();
    e({
      value: s
    });
    const o = typeof M(t.blok.component) != "string", n = ae("VueSDKOptions"), a = O(t.blok.component);
    return !o && n && (n.enableFallbackComponent ? (a.value = n.customFallbackComponent ?? "FallbackComponent", typeof M(a.value) == "string" && console.error(
      `Is the Fallback component "${a.value}" registered properly?`
    )) : console.error(
      `Component could not be found for blok "${t.blok.component}"! Is it defined in main.ts as "app.component("${t.blok.component}", ${t.blok.component});"?`
    )), (l, i) => (Y(), J(M(a.value), ie({
      ref_key: "blokRef",
      ref: s
    }, { ...l.$props, ...l.$attrs }), null, 16));
  }
}), st = (r) => {
  var e, t;
  return W(
    Z,
    {
      blok: (e = r == null ? void 0 : r.attrs) == null ? void 0 : e.body[0],
      id: (t = r.attrs) == null ? void 0 : t.id
    },
    r.children
  );
};
function ot(r) {
  const e = {
    renderFn: W,
    textFn: le,
    keyedResolvers: !0,
    resolvers: {
      [v.COMPONENT]: st,
      ...r.resolvers
    }
  };
  return Ze(e);
}
const nt = /* @__PURE__ */ K({
  __name: "StoryblokRichText",
  props: {
    doc: {},
    resolvers: {}
  },
  setup(r) {
    const e = r, t = O(), s = () => t.value;
    return ce([() => e.doc, () => e.resolvers], ([o, n]) => {
      const { render: a } = ot({
        resolvers: n ?? {}
      });
      t.value = a(o);
    }, {
      immediate: !0,
      deep: !0
    }), (o, n) => (Y(), J(s));
  }
}), at = {
  beforeMount(r, e) {
    if (e.value) {
      const t = Ke(e.value);
      Object.keys(t).length > 0 && (r.setAttribute("data-blok-c", t["data-blok-c"]), r.setAttribute("data-blok-uid", t["data-blok-uid"]), r.classList.add("storyblok__outline"));
    }
  }
}, ee = (r) => {
  console.error(`You can't use ${r} if you're not loading apiPlugin. Please provide it on StoryblokVue initialization.
    `);
};
let x = null;
const ht = () => (x || ee("useStoryblokApi"), x), ut = async (r, e = {}, t = {}) => {
  const s = O(null);
  if (t.resolveRelations = t.resolveRelations ?? e.resolve_relations, t.resolveLinks = t.resolveLinks ?? e.resolve_links, he(() => {
    s.value && s.value.id && et(
      s.value.id,
      (o) => s.value = o,
      t
    );
  }), x) {
    const { data: o } = await x.get(
      `cdn/stories/${r}`,
      e
    );
    s.value = o.story;
  } else
    ee("useStoryblok");
  return s;
}, dt = {
  install(r, e = {}) {
    r.directive("editable", at), r.component("StoryblokComponent", Z), r.component("StoryblokRichText", nt), e.enableFallbackComponent && !e.customFallbackComponent && r.component(
      "FallbackComponent",
      ue(() => import("./FallbackComponent-Dky11gEu.js"))
    );
    const { storyblokApi: t } = tt(e);
    x = t || null, r.provide("VueSDKOptions", e);
  }
};
export {
  v as BlockTypes,
  T as MarkTypes,
  P as RichTextResolver,
  Be as RichTextSchema,
  Z as StoryblokComponent,
  nt as StoryblokRichText,
  dt as StoryblokVue,
  X as TextTypes,
  lt as apiPlugin,
  ct as renderRichText,
  Ze as richTextResolver,
  ut as useStoryblok,
  ht as useStoryblokApi,
  et as useStoryblokBridge,
  ot as useStoryblokRichText
};
